/**
 * JacKey Intent Intelligence — template-specific intros, questions,
 * sentence starters, and gentle unlock prompts for all 7 intents.
 *
 * Each intent drives JacKey's behaviour so the writing guide feels
 * emotionally accurate to the kind of message the user is crafting.
 */

export type IntentSlug =
  | 'apology'
  | 'gratitude'
  | 'hard_conversation'
  | 'love'
  | 'closure'
  | 'custom'
  | 'secret_admire'
  | 'birthday'
  | 'anniversary'
  | 'celebration';

export interface IntentConfig {
  intro: Record<string, string>;
  questions: Record<string, string[]>;
  sentenceStarters: Record<string, string[]>;
  unlockPrompt: Record<string, string>;
}

/* ─────────────────────────────── intents ─────────────────────────────── */

export const INTENT_DATA: Record<IntentSlug, IntentConfig> = {
  apology: {
    intro: {
      en: 'You do not need perfect words to begin taking responsibility.',
      es: 'No necesitas palabras perfectas para empezar a asumir responsabilidad.',
      fr: "Vous n'avez pas besoin de mots parfaits pour commencer à assumer la responsabilité.",
      pt: 'Você não precisa de palavras perfeitas para começar a assumir responsabilidade.',
      ar: 'لا تحتاج كلمات مثالية لتبدأ بتحمل المسؤولية.',
      ja: '完璧な言葉がなくても、責任を取り始めることはできます。',
      ko: '완벽한 말이 없어도 책임을 지기 시작할 수 있습니다.',
      id: 'Anda tidak butuh kata-kata sempurna untuk mulai bertanggung jawab.',
    },
    questions: {
      en: [
        'What happened that you wish you could make right?',
        'What do you most want this person to understand about your regret?',
        'What responsibility are you ready to fully own?',
      ],
      es: [
        '¿Qué pasó que desearías poder arreglar?',
        '¿Qué quieres que esta persona entienda más sobre tu arrepentimiento?',
        '¿Qué responsabilidad estás listo/a para asumir completamente?',
      ],
      fr: [
        "Que s'est-il passé que vous aimeriez pouvoir réparer ?",
        'Que voulez-vous que cette personne comprenne de votre regret ?',
        'Quelle responsabilité êtes-vous prêt(e) à assumer pleinement ?',
      ],
      pt: [
        'O que aconteceu que você gostaria de poder consertar?',
        'O que mais quer que essa pessoa entenda sobre seu arrependimento?',
        'Que responsabilidade está pronto/a para assumir totalmente?',
      ],
      ar: [
        'ما الذي حدث وتتمنى لو تستطيع إصلاحه؟',
        'ما أكثر شيء تريد أن يفهمه هذا الشخص عن ندمك؟',
        'ما المسؤولية التي أنت مستعد لتحملها بالكامل؟',
      ],
      ja: [
        '正しくしたいと思う出来事は何ですか？',
        'あなたの後悔について最も理解してほしいことは何ですか？',
        '完全に引き受ける準備ができている責任は何ですか？',
      ],
      ko: [
        '바로잡고 싶은 일은 무엇인가요?',
        '당신의 후회에 대해 이 사람이 가장 이해해주길 바라는 것은?',
        '완전히 받아들일 준비가 된 책임은 무엇인가요?',
      ],
      id: [
        'Apa yang terjadi yang ingin Anda perbaiki?',
        'Apa yang paling ingin Anda agar orang ini pahami tentang penyesalan Anda?',
        'Tanggung jawab apa yang siap Anda ambil sepenuhnya?',
      ],
    },
    sentenceStarters: {
      en: [
        'I have been thinking about what happened, and I need to say...',
        'You deserved better from me when...',
        'I am sorry not only for what I did, but for...',
      ],
      es: [
        'He estado pensando en lo que pasó, y necesito decir...',
        'Merecías algo mejor de mi parte cuando...',
        'No solo lamento lo que hice, sino también...',
      ],
      fr: [
        "J'ai beaucoup réfléchi à ce qui s'est passé, et je dois dire...",
        'Tu méritais mieux de ma part quand...',
        "Je suis désolé(e) non seulement pour ce que j'ai fait, mais aussi pour...",
      ],
      pt: [
        'Tenho pensado no que aconteceu, e preciso dizer...',
        'Você merecia algo melhor de mim quando...',
        'Lamento não só pelo que fiz, mas por...',
      ],
      ar: [
        'كنت أفكر فيما حدث، وأحتاج أن أقول...',
        'كنت تستحق أفضل مني عندما...',
        'أنا آسف ليس فقط لما فعلته، ولكن أيضاً لـ...',
      ],
      ja: [
        '起きたことについて考えていて、言わなければならないことがあります...',
        'あの時、もっとよくあるべきでした...',
        '自分がしたことだけでなく、...についても申し訳なく思います',
      ],
      ko: [
        '일어난 일에 대해 생각해왔고, 말해야 할 것이 있어요...',
        '그때 당신은 나에게서 더 나은 것을 받을 자격이 있었어요...',
        '내가 한 것뿐만 아니라 ...에 대해서도 미안해요',
      ],
      id: [
        'Saya sudah memikirkan apa yang terjadi, dan saya perlu mengatakan...',
        'Anda layak mendapat yang lebih baik dari saya ketika...',
        'Saya minta maaf bukan hanya atas apa yang saya lakukan, tapi juga atas...',
      ],
    },
    unlockPrompt: {
      en: 'What part feels hardest to admit honestly?',
      es: '¿Qué parte se siente más difícil de admitir honestamente?',
      fr: "Quelle partie est la plus difficile à admettre honnêtement ?",
      pt: 'Qual parte parece mais difícil de admitir honestamente?',
      ar: 'ما الجزء الأصعب في الاعتراف به بصدق؟',
      ja: '正直に認めるのが一番難しい部分は何ですか？',
      ko: '솔직하게 인정하기 가장 어려운 부분은 무엇인가요?',
      id: 'Bagian mana yang paling sulit untuk diakui dengan jujur?',
    },
  },

  gratitude: {
    intro: {
      en: 'Gratitude becomes more powerful when it is specific.',
      es: 'La gratitud se vuelve más poderosa cuando es específica.',
      fr: 'La gratitude devient plus puissante quand elle est spécifique.',
      pt: 'A gratidão se torna mais poderosa quando é específica.',
      ar: 'الامتنان يصبح أقوى عندما يكون محدداً.',
      ja: '感謝は具体的になるほど力強くなります。',
      ko: '감사는 구체적일수록 더 강력해집니다.',
      id: 'Rasa syukur menjadi lebih kuat ketika spesifik.',
    },
    questions: {
      en: [
        'What has this person done that stayed with you?',
        'What would feel incomplete if you never thanked them for it?',
        'What do they deserve to hear clearly from you today?',
      ],
      es: [
        '¿Qué ha hecho esta persona que se quedó contigo?',
        '¿Qué se sentiría incompleto si nunca les agradecieras por ello?',
        '¿Qué merecen escuchar claramente de ti hoy?',
      ],
      fr: [
        "Qu'a fait cette personne qui est resté gravé en vous ?",
        "Que resterait-il incomplet si vous ne les remerciiez jamais ?",
        "Que méritent-ils d'entendre clairement de votre part aujourd'hui ?",
      ],
      pt: [
        'O que essa pessoa fez que ficou com você?',
        'O que pareceria incompleto se você nunca agradecesse por isso?',
        'O que merecem ouvir claramente de você hoje?',
      ],
      ar: [
        'ماذا فعل هذا الشخص وبقي معك؟',
        'ما الذي سيبدو ناقصاً لو لم تشكره أبداً عليه؟',
        'ماذا يستحقون أن يسمعوا بوضوح منك اليوم؟',
      ],
      ja: [
        'この人がしてくれて心に残っていることは何ですか？',
        '感謝を伝えなかったら不完全に感じることは？',
        '今日あなたからはっきり聞く資格があることは何ですか？',
      ],
      ko: [
        '이 사람이 한 일 중 마음에 남은 것은?',
        '감사를 전하지 않으면 불완전하게 느낄 것은?',
        '오늘 당신에게서 분명히 들을 자격이 있는 것은?',
      ],
      id: [
        'Apa yang dilakukan orang ini yang tetap tinggal bersama Anda?',
        'Apa yang akan terasa tidak lengkap jika Anda tidak pernah berterima kasih?',
        'Apa yang mereka layak dengar dengan jelas dari Anda hari ini?',
      ],
    },
    sentenceStarters: {
      en: [
        'I keep thinking about how much it meant when you...',
        'Thank you for being the kind of person who...',
        'You may not realize how deeply your presence has affected my life, but...',
      ],
      es: [
        'Sigo pensando en lo mucho que significó cuando tú...',
        'Gracias por ser el tipo de persona que...',
        'Quizás no te das cuenta de lo profundamente que tu presencia ha afectado mi vida, pero...',
      ],
      fr: [
        "Je n'arrête pas de penser à combien cela comptait quand tu...",
        "Merci d'être le genre de personne qui...",
        "Tu ne réalises peut-être pas à quel point ta présence a affecté ma vie, mais...",
      ],
      pt: [
        'Fico pensando em como foi importante quando você...',
        'Obrigado(a) por ser o tipo de pessoa que...',
        'Talvez você não perceba como sua presença afetou profundamente minha vida, mas...',
      ],
      ar: [
        'لا أزال أفكر في مدى أهمية ما قمت به عندما...',
        'شكراً لكونك الشخص الذي...',
        'ربما لا تدرك مدى تأثير وجودك العميق في حياتي، لكن...',
      ],
      ja: [
        'あなたが...してくれた時、どれほど大きな意味があったか考え続けています',
        '...してくれるような人でいてくれてありがとう',
        'あなたの存在が私の人生にどれほど深く影響したか気づいていないかもしれませんが...',
      ],
      ko: [
        '당신이 ...해줬을 때 얼마나 큰 의미였는지 계속 생각해요',
        '...하는 사람이어서 감사해요',
        '당신의 존재가 내 삶에 얼마나 깊이 영향을 미쳤는지 모를 수도 있지만...',
      ],
      id: [
        'Saya terus memikirkan betapa berartinya saat Anda...',
        'Terima kasih telah menjadi orang yang...',
        'Anda mungkin tidak menyadari betapa dalamnya kehadiran Anda memengaruhi hidup saya, tapi...',
      ],
    },
    unlockPrompt: {
      en: 'What did they do that you still remember clearly?',
      es: '¿Qué hicieron que aún recuerdas con claridad?',
      fr: "Qu'ont-ils fait que vous vous rappelez encore clairement ?",
      pt: 'O que fizeram que você ainda lembra com clareza?',
      ar: 'ماذا فعلوا ولا تزال تتذكره بوضوح؟',
      ja: '今でもはっきり覚えていることは何ですか？',
      ko: '지금도 생생히 기억하는 것은 무엇인가요?',
      id: 'Apa yang mereka lakukan yang masih Anda ingat dengan jelas?',
    },
  },

  hard_conversation: {
    intro: {
      en: 'Honesty is easier when it begins with what is true.',
      es: 'La honestidad es más fácil cuando comienza con lo que es verdad.',
      fr: "L'honnêteté est plus facile quand elle commence par ce qui est vrai.",
      pt: 'A honestidade é mais fácil quando começa com o que é verdade.',
      ar: 'الصدق أسهل عندما يبدأ بما هو حقيقي.',
      ja: '正直さは、真実から始めるとき、より簡単になります。',
      ko: '정직함은 진실에서 시작할 때 더 쉬워집니다.',
      id: 'Kejujuran lebih mudah ketika dimulai dari apa yang benar.',
    },
    questions: {
      en: [
        'What truth have you been carrying that needs to be said?',
        'What makes this conversation difficult?',
        'What do you hope they understand, even if it is hard to hear?',
      ],
      es: [
        '¿Qué verdad has estado cargando que necesita ser dicha?',
        '¿Qué hace difícil esta conversación?',
        '¿Qué esperas que entiendan, aunque sea difícil de escuchar?',
      ],
      fr: [
        'Quelle vérité portez-vous qui doit être dite ?',
        "Qu'est-ce qui rend cette conversation difficile ?",
        "Qu'espérez-vous qu'ils comprennent, même si c'est dur à entendre ?",
      ],
      pt: [
        'Que verdade você tem carregado que precisa ser dita?',
        'O que torna essa conversa difícil?',
        'O que espera que entendam, mesmo que seja difícil de ouvir?',
      ],
      ar: [
        'ما الحقيقة التي حملتها وتحتاج أن تُقال؟',
        'ما الذي يجعل هذه المحادثة صعبة؟',
        'ما الذي تأمل أن يفهموه، حتى لو كان صعب السماع؟',
      ],
      ja: [
        '言わなければならないのに抱え続けてきた真実は何ですか？',
        'この会話を難しくしているものは何ですか？',
        '聞きづらくても、理解してほしいことは何ですか？',
      ],
      ko: [
        '말해야 하는데 안고 있던 진실은 무엇인가요?',
        '이 대화를 어렵게 만드는 것은 무엇인가요?',
        '듣기 어려워도 이해해 주길 바라는 것은?',
      ],
      id: [
        'Kebenaran apa yang Anda bawa yang perlu dikatakan?',
        'Apa yang membuat percakapan ini sulit?',
        'Apa yang Anda harap mereka pahami, meskipun sulit didengar?',
      ],
    },
    sentenceStarters: {
      en: [
        'There is something I have needed to say for a while...',
        'This is hard for me to bring up, but...',
        'What matters most to me in saying this is...',
      ],
      es: [
        'Hay algo que necesito decir desde hace tiempo...',
        'Esto es difícil para mí, pero...',
        'Lo que más me importa al decir esto es...',
      ],
      fr: [
        "Il y a quelque chose que j'ai besoin de dire depuis un moment...",
        "C'est difficile pour moi d'aborder cela, mais...",
        'Ce qui compte le plus pour moi en disant cela est...',
      ],
      pt: [
        'Há algo que preciso dizer há algum tempo...',
        'É difícil para mim trazer isso à tona, mas...',
        'O que mais importa para mim ao dizer isso é...',
      ],
      ar: [
        'هناك شيء أحتاج لقوله منذ فترة...',
        'هذا صعب بالنسبة لي، لكن...',
        'ما يهمني أكثر في قول هذا هو...',
      ],
      ja: [
        'ずっと言わなければならないことがあります...',
        'これを話すのは難しいのですが...',
        'これを伝えることで最も大切にしたいのは...',
      ],
      ko: [
        '오래전부터 말해야 했던 것이 있어요...',
        '이것을 꺼내기 어렵지만...',
        '이 말을 하면서 가장 중요한 것은...',
      ],
      id: [
        'Ada sesuatu yang perlu saya katakan sejak lama...',
        'Ini sulit bagi saya untuk diangkat, tapi...',
        'Yang paling penting bagi saya dalam mengatakan ini adalah...',
      ],
    },
    unlockPrompt: {
      en: 'What truth is trying to come forward?',
      es: '¿Qué verdad está tratando de salir?',
      fr: 'Quelle vérité essaie de se manifester ?',
      pt: 'Que verdade está tentando se manifestar?',
      ar: 'ما الحقيقة التي تحاول الظهور؟',
      ja: '前に出ようとしている真実は何ですか？',
      ko: '앞으로 나오려는 진실은 무엇인가요?',
      id: 'Kebenaran apa yang mencoba muncul?',
    },
  },

  love: {
    intro: {
      en: 'Sometimes the simplest feelings are the ones we delay saying the longest.',
      es: 'A veces los sentimientos más simples son los que más tardamos en decir.',
      fr: 'Parfois les sentiments les plus simples sont ceux que nous tardons le plus à exprimer.',
      pt: 'Às vezes os sentimentos mais simples são os que mais demoramos para dizer.',
      ar: 'أحياناً المشاعر الأبسط هي التي نؤجل قولها أطول وقت.',
      ja: '最もシンプルな気持ちこそ、最も長く伝えることを先延ばしにしてしまうものです。',
      ko: '가장 단순한 감정이 가장 오래 미루게 되는 말이기도 합니다.',
      id: 'Terkadang perasaan paling sederhana adalah yang paling lama kita tunda.',
    },
    questions: {
      en: [
        'What do you love most about this person?',
        'When did you last feel especially grateful they were in your life?',
        'What do they deserve to hear from you now?',
      ],
      es: [
        '¿Qué es lo que más amas de esta persona?',
        '¿Cuándo fue la última vez que te sentiste especialmente agradecido/a por tenerle en tu vida?',
        '¿Qué merecen escuchar de ti ahora?',
      ],
      fr: [
        "Qu'aimez-vous le plus chez cette personne ?",
        "Quand vous êtes-vous senti(e) particulièrement reconnaissant(e) de l'avoir dans votre vie ?",
        "Que méritent-ils d'entendre de vous maintenant ?",
      ],
      pt: [
        'O que mais ama nesta pessoa?',
        'Quando se sentiu especialmente grato/a por tê-la na sua vida?',
        'O que merecem ouvir de você agora?',
      ],
      ar: [
        'ما أكثر شيء تحبه في هذا الشخص؟',
        'متى شعرت آخر مرة بامتنان خاص لوجوده في حياتك؟',
        'ماذا يستحقون أن يسمعوا منك الآن؟',
      ],
      ja: [
        'この人の一番好きなところは何ですか？',
        '最後にこの人がいてくれて特に感謝したのはいつですか？',
        '今あなたから聞く資格があることは何ですか？',
      ],
      ko: [
        '이 사람에 대해 가장 사랑하는 것은?',
        '마지막으로 이 사람이 삶에 있어 특히 감사했던 때는?',
        '지금 당신에게서 들을 자격이 있는 것은?',
      ],
      id: [
        'Apa yang paling Anda cintai dari orang ini?',
        'Kapan terakhir Anda merasa sangat bersyukur mereka ada dalam hidup Anda?',
        'Apa yang mereka layak dengar dari Anda sekarang?',
      ],
    },
    sentenceStarters: {
      en: [
        'I want you to know how much you mean to me...',
        'One of the things I love most about you is...',
        'I do not want more time to pass without telling you...',
      ],
      es: [
        'Quiero que sepas lo mucho que significas para mí...',
        'Una de las cosas que más amo de ti es...',
        'No quiero que pase más tiempo sin decirte...',
      ],
      fr: [
        'Je veux que tu saches combien tu comptes pour moi...',
        "L'une des choses que j'aime le plus chez toi est...",
        'Je ne veux pas que plus de temps passe sans te dire...',
      ],
      pt: [
        'Quero que saiba o quanto significa para mim...',
        'Uma das coisas que mais amo em você é...',
        'Não quero que mais tempo passe sem te dizer...',
      ],
      ar: [
        'أريدك أن تعرف كم تعني لي...',
        'أحد الأشياء التي أحبها فيك أكثر هو...',
        'لا أريد أن يمر المزيد من الوقت دون أن أقول لك...',
      ],
      ja: [
        'あなたが私にとってどれほど大切か知ってほしい...',
        'あなたについて最も好きなことのひとつは...',
        'もうこれ以上伝えずに時間を過ごしたくない...',
      ],
      ko: [
        '당신이 나에게 얼마나 의미 있는지 알아주세요...',
        '당신에 대해 가장 좋아하는 것 중 하나는...',
        '더 이상 시간이 지나기 전에 말하고 싶어요...',
      ],
      id: [
        'Saya ingin Anda tahu betapa berartinya Anda bagi saya...',
        'Salah satu hal yang paling saya cintai dari Anda adalah...',
        'Saya tidak ingin lebih banyak waktu berlalu tanpa memberitahu Anda...',
      ],
    },
    unlockPrompt: {
      en: 'What do you never want them to doubt?',
      es: '¿Qué no quieres que duden nunca?',
      fr: "De quoi ne voulez-vous jamais qu'ils doutent ?",
      pt: 'O que nunca quer que duvidem?',
      ar: 'ما الذي لا تريدهم أن يشكوا فيه أبداً؟',
      ja: '彼らに絶対に疑ってほしくないことは何ですか？',
      ko: '절대 의심하지 않았으면 하는 것은 무엇인가요?',
      id: 'Apa yang Anda tidak ingin mereka ragukan?',
    },
  },

  closure: {
    intro: {
      en: 'Closure does not erase pain. It gives your heart a place to set it down.',
      es: 'El cierre no borra el dolor. Le da a tu corazón un lugar donde dejarlo.',
      fr: "La clôture n'efface pas la douleur. Elle donne à votre cœur un endroit pour la déposer.",
      pt: 'O encerramento não apaga a dor. Dá ao seu coração um lugar para depositá-la.',
      ar: 'الإغلاق لا يمحو الألم. إنه يعطي قلبك مكاناً ليضعه فيه.',
      ja: '区切りは痛みを消しません。心に痛みを置く場所を与えてくれるのです。',
      ko: '마무리는 아픔을 지우지 않습니다. 마음에 그것을 내려놓을 곳을 줍니다.',
      id: 'Penutupan tidak menghapus rasa sakit. Ia memberi hati Anda tempat untuk meletakkannya.',
    },
    questions: {
      en: [
        'What are you ready to make peace with?',
        'What feels unresolved that you want to gently name?',
        'What would help you leave this message feeling lighter?',
      ],
      es: [
        '¿Con qué estás listo/a para hacer las paces?',
        '¿Qué se siente sin resolver y quieres nombrar con suavidad?',
        '¿Qué te ayudaría a dejar este mensaje sintiéndote más ligero/a?',
      ],
      fr: [
        'Avec quoi êtes-vous prêt(e) à faire la paix ?',
        "Qu'est-ce qui semble non résolu que vous voulez nommer doucement ?",
        'Que vous aiderait à quitter ce message en vous sentant plus léger(e) ?',
      ],
      pt: [
        'Com o que está pronto/a para fazer as pazes?',
        'O que parece não resolvido que quer nomear suavemente?',
        'O que ajudaria você a sair desta mensagem se sentindo mais leve?',
      ],
      ar: [
        'ما الذي أنت مستعد للتصالح معه؟',
        'ما الذي يبدو غير محلول وتريد تسميته بلطف؟',
        'ما الذي سيساعدك على ترك هذه الرسالة وأنت تشعر بخفة؟',
      ],
      ja: [
        '和解する準備ができていることは何ですか？',
        '未解決に感じていて、そっと名前をつけたいことは？',
        'このメッセージを書き終えた後、心が軽くなるために何が必要ですか？',
      ],
      ko: [
        '화해할 준비가 된 것은 무엇인가요?',
        '해결되지 않은 채 부드럽게 말하고 싶은 것은?',
        '이 메시지를 마치고 가벼워지려면 무엇이 도움이 될까요?',
      ],
      id: [
        'Apa yang siap Anda damaikan?',
        'Apa yang terasa belum terselesaikan yang ingin Anda sebutkan dengan lembut?',
        'Apa yang akan membantu Anda meninggalkan pesan ini dengan perasaan lebih ringan?',
      ],
    },
    sentenceStarters: {
      en: [
        'I have been carrying this for a long time...',
        'I want to leave this message with honesty and peace...',
        'There are some things I no longer want to leave unresolved...',
      ],
      es: [
        'He cargado con esto durante mucho tiempo...',
        'Quiero dejar este mensaje con honestidad y paz...',
        'Hay algunas cosas que ya no quiero dejar sin resolver...',
      ],
      fr: [
        'Je porte cela depuis longtemps...',
        'Je veux laisser ce message avec honnêteté et paix...',
        'Il y a des choses que je ne veux plus laisser non résolues...',
      ],
      pt: [
        'Tenho carregado isso por muito tempo...',
        'Quero deixar esta mensagem com honestidade e paz...',
        'Há algumas coisas que não quero mais deixar sem resolver...',
      ],
      ar: [
        'لقد حملت هذا لفترة طويلة...',
        'أريد أن أترك هذه الرسالة بصدق وسلام...',
        'هناك بعض الأشياء التي لا أريد تركها دون حل...',
      ],
      ja: [
        'これを長い間抱えてきました...',
        '正直さと平和をもってこのメッセージを残したい...',
        'もう未解決のままにしておきたくないことがあります...',
      ],
      ko: [
        '오랫동안 이것을 안고 있었어요...',
        '정직함과 평화로 이 메시지를 남기고 싶어요...',
        '더 이상 미해결로 남기고 싶지 않은 것들이 있어요...',
      ],
      id: [
        'Saya sudah membawa ini untuk waktu yang lama...',
        'Saya ingin meninggalkan pesan ini dengan kejujuran dan kedamaian...',
        'Ada beberapa hal yang tidak ingin saya biarkan tak terselesaikan lagi...',
      ],
    },
    unlockPrompt: {
      en: 'What do you need to put down so you can breathe easier?',
      es: '¿Qué necesitas soltar para poder respirar más fácil?',
      fr: 'Que devez-vous déposer pour respirer plus facilement ?',
      pt: 'O que precisa soltar para poder respirar mais facilmente?',
      ar: 'ما الذي تحتاج لوضعه جانباً لتتنفس بسهولة؟',
      ja: '楽に呼吸するために手放す必要があるものは何ですか？',
      ko: '더 편하게 숨 쉬려면 무엇을 내려놓아야 하나요?',
      id: 'Apa yang perlu Anda lepaskan agar bisa bernapas lebih mudah?',
    },
  },

  custom: {
    intro: {
      en: 'You do not have to fit your feelings into a category to say something meaningful.',
      es: 'No tienes que encajar tus sentimientos en una categoría para decir algo significativo.',
      fr: "Vous n'avez pas besoin de faire entrer vos sentiments dans une catégorie pour dire quelque chose de significatif.",
      pt: 'Você não precisa encaixar seus sentimentos em uma categoria para dizer algo significativo.',
      ar: 'لا تحتاج أن تضع مشاعرك في فئة لتقول شيئاً ذا معنى.',
      ja: '意味のあることを伝えるために、気持ちをカテゴリーに当てはめる必要はありません。',
      ko: '의미 있는 말을 하기 위해 감정을 카테고리에 맞출 필요는 없습니다.',
      id: 'Anda tidak harus memasukkan perasaan ke dalam kategori untuk mengatakan sesuatu yang bermakna.',
    },
    questions: {
      en: [
        'What has been on your heart lately?',
        'What would you regret leaving unsaid?',
        'If they could hear one honest thing from you today, what should it be?',
      ],
      es: [
        '¿Qué ha estado en tu corazón últimamente?',
        '¿Qué lamentarías dejar sin decir?',
        'Si pudieran escuchar algo honesto de ti hoy, ¿qué debería ser?',
      ],
      fr: [
        "Qu'avez-vous sur le cœur ces derniers temps ?",
        "Que regretteriez-vous de ne pas avoir dit ?",
        "S'ils pouvaient entendre une chose honnête de vous aujourd'hui, quelle serait-elle ?",
      ],
      pt: [
        'O que tem estado em seu coração ultimamente?',
        'O que lamentaria deixar sem dizer?',
        'Se pudessem ouvir algo honesto de você hoje, o que deveria ser?',
      ],
      ar: [
        'ما الذي كان على قلبك مؤخراً؟',
        'ما الذي ستندم لو تركته دون قول؟',
        'لو استطاعوا سماع شيء صادق منك اليوم، ماذا يجب أن يكون؟',
      ],
      ja: [
        '最近心にあることは何ですか？',
        '言わずにいたら後悔することは何ですか？',
        '今日あなたから正直なことをひとつ聞けるとしたら、何であるべきですか？',
      ],
      ko: [
        '최근 마음에 담고 있는 것은 무엇인가요?',
        '말하지 않으면 후회할 것은?',
        '오늘 당신에게서 솔직한 한 마디를 들을 수 있다면, 무엇이어야 할까요?',
      ],
      id: [
        'Apa yang ada di hati Anda akhir-akhir ini?',
        'Apa yang akan Anda sesali jika tidak tersampaikan?',
        'Jika mereka bisa mendengar satu hal jujur dari Anda hari ini, apa seharusnya?',
      ],
    },
    sentenceStarters: {
      en: [
        'I have been wanting to say...',
        'What is on my heart is...',
        'I do not want to leave this unsaid...',
      ],
      es: [
        'He querido decir...',
        'Lo que hay en mi corazón es...',
        'No quiero dejar esto sin decir...',
      ],
      fr: [
        'Je voulais dire...',
        'Ce qui est dans mon cœur est...',
        'Je ne veux pas laisser cela non-dit...',
      ],
      pt: [
        'Tenho querido dizer...',
        'O que está em meu coração é...',
        'Não quero deixar isso sem dizer...',
      ],
      ar: [
        'كنت أريد أن أقول...',
        'ما في قلبي هو...',
        'لا أريد أن أترك هذا دون أن أقوله...',
      ],
      ja: [
        'ずっと言いたかったのは...',
        '心にあるのは...',
        'これを言わずにいたくない...',
      ],
      ko: [
        '말하고 싶었던 것은...',
        '마음에 있는 것은...',
        '이것을 말하지 않고 두고 싶지 않아요...',
      ],
      id: [
        'Saya sudah ingin mengatakan...',
        'Yang ada di hati saya adalah...',
        'Saya tidak ingin membiarkan ini tidak tersampaikan...',
      ],
    },
    unlockPrompt: {
      en: 'What truth is trying to come forward?',
      es: '¿Qué verdad está tratando de salir?',
      fr: 'Quelle vérité essaie de se manifester ?',
      pt: 'Que verdade está tentando se manifestar?',
      ar: 'ما الحقيقة التي تحاول الظهور؟',
      ja: '前に出ようとしている真実は何ですか？',
      ko: '앞으로 나오려는 진실은 무엇인가요?',
      id: 'Kebenaran apa yang mencoba muncul?',
    },
  },

  secret_admire: {
    intro: {
      en: 'Courage can be quiet and still be real.',
      es: 'El valor puede ser silencioso y aun así ser real.',
      fr: 'Le courage peut être silencieux et rester réel.',
      pt: 'A coragem pode ser silenciosa e ainda ser real.',
      ar: 'الشجاعة يمكن أن تكون هادئة وتبقى حقيقية.',
      ja: '勇気は静かでも本物であり得ます。',
      ko: '용기는 조용해도 진짜일 수 있습니다.',
      id: 'Keberanian bisa tenang dan tetap nyata.',
    },
    questions: {
      en: [
        'What is it about this person that draws you to them?',
        'What do you want them to feel when they read this?',
        'Do you want to be playful, sincere, mysterious, or direct?',
      ],
      es: [
        '¿Qué tiene esta persona que te atrae hacia ella?',
        '¿Qué quieres que sienta cuando lea esto?',
        '¿Quieres ser juguetón/a, sincero/a, misterioso/a o directo/a?',
      ],
      fr: [
        "Qu'est-ce qui vous attire chez cette personne ?",
        "Que voulez-vous qu'ils ressentent en lisant ceci ?",
        'Voulez-vous être ludique, sincère, mystérieux ou direct ?',
      ],
      pt: [
        'O que essa pessoa tem que te atrai?',
        'O que quer que sintam ao ler isso?',
        'Quer ser divertido/a, sincero/a, misterioso/a ou direto/a?',
      ],
      ar: [
        'ما الذي يجذبك في هذا الشخص؟',
        'ماذا تريدهم أن يشعروا عند قراءة هذا؟',
        'هل تريد أن تكون مرحاً، صادقاً، غامضاً أم مباشراً؟',
      ],
      ja: [
        'この人のどこに惹かれますか？',
        'これを読んだ時にどう感じてほしいですか？',
        '遊び心、誠実、ミステリアス、直接的のどれがいいですか？',
      ],
      ko: [
        '이 사람의 어떤 점에 끌리나요?',
        '이것을 읽었을 때 어떤 감정을 느끼길 바라나요?',
        '장난스럽게, 진심으로, 신비롭게, 직접적으로 중 어떤 것을 원하나요?',
      ],
      id: [
        'Apa dari orang ini yang menarik Anda?',
        'Apa yang Anda ingin mereka rasakan saat membaca ini?',
        'Ingin yang playful, tulus, misterius, atau langsung?',
      ],
    },
    sentenceStarters: {
      en: [
        'I have been meaning to tell you...',
        'You may not realize it, but...',
        'There is something I have wanted to say quietly for a while...',
      ],
      es: [
        'He querido decirte esto...',
        'Quizás no lo sabes, pero...',
        'Hay algo que he querido decir en silencio desde hace un tiempo...',
      ],
      fr: [
        'Je voulais te dire...',
        'Tu ne le réalises peut-être pas, mais...',
        "Il y a quelque chose que je voulais dire doucement depuis un moment...",
      ],
      pt: [
        'Tenho querido te dizer...',
        'Talvez você não perceba, mas...',
        'Há algo que quero dizer silenciosamente há um tempo...',
      ],
      ar: [
        'كنت أريد أن أقول لك...',
        'ربما لا تدرك ذلك، لكن...',
        'هناك شيء أردت قوله بهدوء منذ فترة...',
      ],
      ja: [
        'ずっと伝えたかったのですが...',
        '気づいていないかもしれませんが...',
        'しばらく静かに伝えたいことがありました...',
      ],
      ko: [
        '말하려고 했던 것이 있어요...',
        '아마 모르시겠지만...',
        '조용히 말하고 싶었던 것이 있어요...',
      ],
      id: [
        'Saya sudah ingin memberitahu Anda...',
        'Anda mungkin tidak menyadarinya, tapi...',
        'Ada sesuatu yang ingin saya katakan secara diam-diam sejak lama...',
      ],
    },
    unlockPrompt: {
      en: 'What do you want them to feel when they finish reading?',
      es: '¿Qué quieres que sientan cuando terminen de leer?',
      fr: "Que voulez-vous qu'ils ressentent en finissant de lire ?",
      pt: 'O que quer que sintam ao terminar de ler?',
      ar: 'ماذا تريدهم أن يشعروا عند انتهائهم من القراءة؟',
      ja: '読み終えた時にどう感じてほしいですか？',
      ko: '다 읽었을 때 어떤 감정을 느끼길 바라나요?',
      id: 'Apa yang Anda ingin mereka rasakan saat selesai membaca?',
    },
  },

  birthday: {
    intro: {
      en: 'Say something they will remember long after today.',
      es: 'Di algo que recordarán mucho después de hoy.',
      fr: "Dites quelque chose dont ils se souviendront longtemps après aujourd'hui.",
      pt: 'Diga algo que eles vão lembrar muito depois de hoje.',
      ar: 'قل شيئاً سيتذكرونه طويلاً بعد اليوم.',
      ja: '今日の後もずっと覚えていてもらえる言葉を伝えましょう。',
      ko: '오늘이 지나도 오래 기억될 말을 전하세요.',
      id: 'Katakan sesuatu yang akan mereka ingat lama setelah hari ini.',
    },
    questions: {
      en: [
        'What do you want them to remember on their special day?',
        'What makes them one of a kind?',
        'What year or moment are you proud of them for?',
        'What blessing or wish do you want to speak over them?',
      ],
      es: [
        '¿Qué quieres que recuerden en su día especial?',
        '¿Qué los hace únicos?',
        '¿De qué año o momento estás orgulloso/a de ellos?',
        '¿Qué bendición o deseo quieres expresarles?',
      ],
      fr: [
        "Que voulez-vous qu'ils retiennent de leur journée spéciale ?",
        "Qu'est-ce qui les rend uniques ?",
        'De quelle année ou moment êtes-vous fier(e) pour eux ?',
        'Quelle bénédiction ou souhait voulez-vous leur adresser ?',
      ],
      pt: [
        'O que quer que lembrem no seu dia especial?',
        'O que os torna únicos?',
        'De que ano ou momento você tem orgulho deles?',
        'Que bênção ou desejo quer expressar a eles?',
      ],
      ar: [
        'ما الذي تريدهم أن يتذكروه في يومهم المميز؟',
        'ما الذي يجعلهم فريدين؟',
        'أي سنة أو لحظة تفخر بهم فيها؟',
        'ما البركة أو الأمنية التي تريد أن تقولها لهم؟',
      ],
      ja: [
        '特別な日に覚えていてほしいことは何ですか？',
        '彼らをかけがえのない存在にしているものは？',
        'どの年や瞬間を誇りに思いますか？',
        'どんな祝福や願いを伝えたいですか？',
      ],
      ko: [
        '특별한 날에 기억해 주길 바라는 것은?',
        '그들을 특별하게 만드는 것은?',
        '어떤 해나 순간이 자랑스러운가요?',
        '어떤 축복이나 소원을 전하고 싶나요?',
      ],
      id: [
        'Apa yang ingin mereka ingat di hari spesial mereka?',
        'Apa yang membuat mereka satu-satunya?',
        'Tahun atau momen apa yang Anda banggakan dari mereka?',
        'Berkat atau harapan apa yang ingin Anda ucapkan untuk mereka?',
      ],
    },
    sentenceStarters: {
      en: [
        'On your birthday, I want you to know...',
        'Every year with you reminds me that...',
        'If I could give you one thing today, it would be...',
      ],
      es: [
        'En tu cumpleaños, quiero que sepas...',
        'Cada año contigo me recuerda que...',
        'Si pudiera darte algo hoy, sería...',
      ],
      fr: [
        "Pour ton anniversaire, je veux que tu saches...",
        'Chaque année avec toi me rappelle que...',
        "Si je pouvais te donner une chose aujourd'hui, ce serait...",
      ],
      pt: [
        'No seu aniversário, quero que saiba...',
        'Cada ano com você me lembra que...',
        'Se pudesse te dar uma coisa hoje, seria...',
      ],
      ar: [
        'في عيد ميلادك، أريدك أن تعرف...',
        'كل سنة معك تذكرني أن...',
        'لو استطعت أن أعطيك شيئاً واحداً اليوم، سيكون...',
      ],
      ja: [
        'あなたの誕生日に、知ってほしいことがあります...',
        '毎年あなたと過ごすたびに思い出すのは...',
        '今日一つだけ贈れるとしたら、それは...',
      ],
      ko: [
        '생일에 알아주셨으면 하는 것이 있어요...',
        '매년 함께하면서 떠오르는 것은...',
        '오늘 하나만 줄 수 있다면, 그것은...',
      ],
      id: [
        'Di hari ulang tahunmu, aku ingin kamu tahu...',
        'Setiap tahun bersamamu mengingatkan bahwa...',
        'Jika aku bisa memberimu satu hal hari ini, itu adalah...',
      ],
    },
    unlockPrompt: {
      en: 'What makes this birthday different from any other?',
      es: '¿Qué hace que este cumpleaños sea diferente a cualquier otro?',
      fr: "Qu'est-ce qui rend cet anniversaire différent des autres ?",
      pt: 'O que torna este aniversário diferente de qualquer outro?',
      ar: 'ما الذي يجعل عيد الميلاد هذا مختلفاً عن أي آخر؟',
      ja: 'この誕生日を他と違うものにしているのは何ですか？',
      ko: '이번 생일이 다른 생일과 다른 점은 무엇인가요?',
      id: 'Apa yang membuat ulang tahun ini berbeda dari yang lain?',
    },
  },

  anniversary: {
    intro: {
      en: 'Honor the story only the two of you share.',
      es: 'Honra la historia que solo ustedes dos comparten.',
      fr: "Honorez l'histoire que vous seuls partagez.",
      pt: 'Honre a história que só vocês dois compartilham.',
      ar: 'كرّم القصة التي تشاركانها أنتما فقط.',
      ja: '二人だけが共有する物語を称えましょう。',
      ko: '둘만이 공유하는 이야기를 기리세요.',
      id: 'Hormati kisah yang hanya kalian berdua yang tahu.',
    },
    questions: {
      en: [
        'What journey have you shared together?',
        'What has this relationship meant to you?',
        'What moments define your bond?',
        'What do you want to promise, honor, or remember?',
      ],
      es: [
        '¿Qué camino han recorrido juntos?',
        '¿Qué ha significado esta relación para ti?',
        '¿Qué momentos definen su vínculo?',
        '¿Qué quieres prometer, honrar o recordar?',
      ],
      fr: [
        'Quel chemin avez-vous parcouru ensemble ?',
        "Qu'a signifié cette relation pour vous ?",
        'Quels moments définissent votre lien ?',
        'Que voulez-vous promettre, honorer ou vous rappeler ?',
      ],
      pt: [
        'Que jornada vocês compartilharam?',
        'O que essa relação significou para você?',
        'Que momentos definem o vínculo de vocês?',
        'O que quer prometer, honrar ou lembrar?',
      ],
      ar: [
        'ما الرحلة التي شاركتماها معاً؟',
        'ماذا تعني هذه العلاقة لك؟',
        'ما اللحظات التي تحدد رابطتكما؟',
        'ماذا تريد أن تعد، تكرم، أو تتذكر؟',
      ],
      ja: [
        '一緒に歩んできた旅路は？',
        'この関係はあなたにとって何を意味しますか？',
        '絆を定義する瞬間は？',
        '何を約束し、称え、または覚えていたいですか？',
      ],
      ko: [
        '함께 걸어온 여정은 무엇인가요?',
        '이 관계가 당신에게 어떤 의미인가요?',
        '유대를 정의하는 순간은?',
        '무엇을 약속하고, 기리고, 기억하고 싶나요?',
      ],
      id: [
        'Perjalanan apa yang telah kalian lalui bersama?',
        'Apa arti hubungan ini bagi Anda?',
        'Momen apa yang mendefinisikan ikatan kalian?',
        'Apa yang ingin Anda janjikan, hormati, atau kenang?',
      ],
    },
    sentenceStarters: {
      en: [
        'Looking back on our journey, what I treasure most is...',
        'You have taught me that love means...',
        'With every year together, I realize more that...',
      ],
      es: [
        'Mirando atrás en nuestro camino, lo que más valoro es...',
        'Me has enseñado que el amor significa...',
        'Con cada año juntos, me doy más cuenta de que...',
      ],
      fr: [
        'En repensant à notre parcours, ce que je chéris le plus est...',
        "Tu m'as appris que l'amour signifie...",
        'Avec chaque année ensemble, je réalise davantage que...',
      ],
      pt: [
        'Olhando para nossa jornada, o que mais valorizo é...',
        'Você me ensinou que amor significa...',
        'A cada ano juntos, percebo mais que...',
      ],
      ar: [
        'بالنظر إلى رحلتنا، ما أعتز به أكثر هو...',
        'علمتني أن الحب يعني...',
        'مع كل سنة معاً، أدرك أكثر أن...',
      ],
      ja: [
        '私たちの旅を振り返ると、一番大切にしているのは...',
        'あなたは愛が...ということを教えてくれました',
        '一緒に過ごす年ごとに、ますます気づくのは...',
      ],
      ko: [
        '우리의 여정을 돌아보면, 가장 소중한 것은...',
        '당신은 사랑이 ...라는 것을 가르쳐 주었어요',
        '함께한 해가 쌓일수록 더 깨닫는 것은...',
      ],
      id: [
        'Melihat perjalanan kita, yang paling saya hargai adalah...',
        'Kamu mengajarkan bahwa cinta berarti...',
        'Dengan setiap tahun bersama, saya semakin menyadari bahwa...',
      ],
    },
    unlockPrompt: {
      en: 'What moment together would you relive if you could?',
      es: '¿Qué momento juntos revivirías si pudieras?',
      fr: 'Quel moment ensemble revivriez-vous si vous pouviez ?',
      pt: 'Que momento juntos você reviveria se pudesse?',
      ar: 'أي لحظة معاً ستعيدها لو استطعت؟',
      ja: 'もう一度体験できるとしたら、一緒のどの瞬間を選びますか？',
      ko: '다시 경험할 수 있다면, 함께한 어떤 순간을 선택하겠나요?',
      id: 'Momen bersama mana yang akan Anda ulangi jika bisa?',
    },
  },

  celebration: {
    intro: {
      en: 'Make them feel seen, celebrated, and unforgettable.',
      es: 'Hazlos sentir vistos, celebrados e inolvidables.',
      fr: 'Faites-les se sentir vus, célébrés et inoubliables.',
      pt: 'Faça-os se sentir vistos, celebrados e inesquecíveis.',
      ar: 'اجعلهم يشعرون بأنهم مرئيون ومحتفى بهم ولا يُنسون.',
      ja: '見てもらえている、祝われている、忘れられないと感じさせましょう。',
      ko: '그들이 인정받고, 축하받고, 잊을 수 없다고 느끼게 하세요.',
      id: 'Buat mereka merasa dilihat, dirayakan, dan tak terlupakan.',
    },
    questions: {
      en: [
        'What makes this person worth celebrating?',
        'What do they bring into your life?',
        'What moment with them makes you smile?',
        'What do you want them to feel when they read this?',
      ],
      es: [
        '¿Qué hace que esta persona merezca ser celebrada?',
        '¿Qué aportan a tu vida?',
        '¿Qué momento con ellos te hace sonreír?',
        '¿Qué quieres que sientan al leer esto?',
      ],
      fr: [
        "Qu'est-ce qui rend cette personne digne d'être célébrée ?",
        "Qu'apportent-ils dans votre vie ?",
        'Quel moment avec eux vous fait sourire ?',
        "Que voulez-vous qu'ils ressentent en lisant ceci ?",
      ],
      pt: [
        'O que torna esta pessoa digna de ser celebrada?',
        'O que trazem para sua vida?',
        'Que momento com eles te faz sorrir?',
        'O que quer que sintam ao ler isso?',
      ],
      ar: [
        'ما الذي يجعل هذا الشخص يستحق الاحتفاء؟',
        'ماذا يضيفون لحياتك؟',
        'ما اللحظة معهم التي تجعلك تبتسم؟',
        'ماذا تريدهم أن يشعروا عند قراءة هذا؟',
      ],
      ja: [
        'この人を祝う価値があるのはなぜですか？',
        'あなたの人生に何をもたらしてくれますか？',
        '一緒にいて笑顔になる瞬間は？',
        'これを読んだ時にどう感じてほしいですか？',
      ],
      ko: [
        '이 사람이 축하받을 만한 이유는?',
        '당신의 삶에 무엇을 가져다 주나요?',
        '함께한 어떤 순간이 미소 짓게 하나요?',
        '이것을 읽었을 때 어떤 감정을 느끼길 바라나요?',
      ],
      id: [
        'Apa yang membuat orang ini layak dirayakan?',
        'Apa yang mereka bawa ke hidup Anda?',
        'Momen apa bersama mereka yang membuat Anda tersenyum?',
        'Apa yang Anda ingin mereka rasakan saat membaca ini?',
      ],
    },
    sentenceStarters: {
      en: [
        'I want to celebrate you because...',
        'You deserve to know that...',
        'The way you show up in the world inspires me because...',
      ],
      es: [
        'Quiero celebrarte porque...',
        'Mereces saber que...',
        'La forma en que te presentas al mundo me inspira porque...',
      ],
      fr: [
        'Je veux te célébrer parce que...',
        'Tu mérites de savoir que...',
        'La façon dont tu te montres au monde me inspire parce que...',
      ],
      pt: [
        'Quero celebrar você porque...',
        'Você merece saber que...',
        'A forma como você se apresenta ao mundo me inspira porque...',
      ],
      ar: [
        'أريد أن أحتفي بك لأن...',
        'تستحق أن تعرف أن...',
        'الطريقة التي تظهر بها للعالم تلهمني لأن...',
      ],
      ja: [
        'あなたを祝いたいのは...',
        '知ってほしいのは...',
        'あなたの生き方に触発されるのは...',
      ],
      ko: [
        '당신을 축하하고 싶은 이유는...',
        '알아주셨으면 하는 것은...',
        '당신이 세상에 나타나는 방식이 영감을 주는 이유는...',
      ],
      id: [
        'Saya ingin merayakanmu karena...',
        'Kamu layak tahu bahwa...',
        'Cara kamu hadir di dunia menginspirasi karena...',
      ],
    },
    unlockPrompt: {
      en: 'What would you want them to always remember about themselves?',
      es: '¿Qué te gustaría que siempre recordaran sobre sí mismos?',
      fr: "Que voudriez-vous qu'ils se rappellent toujours à propos d'eux-mêmes ?",
      pt: 'O que gostaria que sempre lembrassem sobre si mesmos?',
      ar: 'ماذا تريدهم أن يتذكروا دائماً عن أنفسهم؟',
      ja: '自分自身について常に覚えていてほしいことは？',
      ko: '자신에 대해 항상 기억해 주길 바라는 것은?',
      id: 'Apa yang Anda ingin mereka selalu ingat tentang diri mereka?',
    },
  },
};

/* ─────────────────── helpers ─────────────────── */

/**
 * Resolve the intent slug from template + relationship.
 * crush relationship → secret_admire, otherwise use the template value.
 */
export function resolveIntentSlug(
  template: string,
  relationship: string | null,
): IntentSlug {
  if (relationship === 'crush') return 'secret_admire';
  if (template in INTENT_DATA) return template as IntentSlug;
  return 'custom';
}

/** Get a localized string from a lang→string map, falling back to English. */
export function lstr(dict: Record<string, string>, lang: string): string {
  return dict[lang] ?? dict.en ?? '';
}

/** Get a localized string array from a lang→string[] map, falling back to English. */
export function lstrArr(dict: Record<string, string[]>, lang: string): string[] {
  return dict[lang] ?? dict.en ?? [];
}
